import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { PdfDownloadMenuOptions } from 'features/knowledgeDocumentViewer/components/toolbar/PdfDownloadMenuOptions'
import { useDocumentDownloadActions } from 'features/knowledgeDocumentViewer/hooks/useDocumentDownloadActions'
import { Download } from 'lucide-react'
import type { ComponentProps, MouseEvent } from 'react'

import { useWorkOrderPdf } from './useWorkOrderPdf'
import { cmmsRecordNumber } from './workOrder'

export function WorkOrderDownloadButton({
  documentId,
  pageNumber,
  size = 'icon-lg',
  showPageMenu = false,
  currentPage = 1,
}: {
  documentId: string
  pageNumber?: number
  showPageMenu?: boolean
  currentPage?: number
  size?: ComponentProps<typeof Button>['size']
}) {
  const { t } = useLingui()
  const { entry, pdfUrl, pdf } = useWorkOrderPdf(documentId)
  const {
    isDownloading,
    handleFullPdfDownload,
    handleCurrentPageDownload,
    handleRangeDownload,
    handleOutlineUnitDownload,
  } = useDocumentDownloadActions({
    pdfSignedUrl: pdfUrl,
    originalSignedUrl: undefined,
    documentFilename: `${entry ? cmmsRecordNumber(entry) : 'cmms-record'}.pdf`,
    currentPageNumber: pageNumber ?? currentPage,
  })
  const download = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    void (pageNumber ? handleCurrentPageDownload() : handleFullPdfDownload())
  }
  if (showPageMenu && pdf && pdf.pageCount > 1)
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='inputRaised'
            size={size}
            disabled={!pdfUrl || isDownloading}
            title={t`Download PDF`}
            aria-label={t`Download PDF`}>
            <Download />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <PdfDownloadMenuOptions
            variant='pdf-document'
            totalPages={pdf.pageCount}
            currentPageNumber={currentPage}
            pdfSignedUrl={pdfUrl}
            isDownloading={isDownloading}
            outlineUnits={[]}
            isOutlineLoading={false}
            onCurrentPage={handleCurrentPageDownload}
            onFullPdf={handleFullPdfDownload}
            onRange={handleRangeDownload}
            onOutlineUnit={handleOutlineUnitDownload}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    )
  return (
    <Button
      variant='inputRaised'
      size={size}
      disabled={!pdfUrl || isDownloading}
      title={pageNumber ? t`Download Page ${pageNumber}` : t`Download PDF`}
      aria-label={t`Download PDF`}
      onClick={download}>
      <Download />
    </Button>
  )
}
